import type { TesterRecentJob } from '@runhuman/shared';
import { useTesterRecentJobs } from '../../../use-tester-profile';

interface HistoryTabProps {
  readonly accessToken: string;
  readonly testerId: string;
}

/**
 * Activity-side mirror of `<InternalTesterProfileDialog>` HistoryTab. Lists
 * the tester's most-recent completed jobs (project, status, duration, short
 * description). Mounted only when `capabilities.isAdmin`; the underlying
 * route gates server-side too — admin or self only — so a non-admin viewer
 * who somehow reached the URL would get 403.
 *
 * No "View job" deep-link in the Activity — the iframe has no admin URL
 * fragments to route to. Admins who need the full job detail can reach it
 * from the web admin portal.
 */
export function HistoryTab({ accessToken, testerId }: HistoryTabProps) {
  const { data, isLoading, error } = useTesterRecentJobs(accessToken, testerId);

  if (isLoading || !data) {
    return <div className="profile-modal__loading">Loading…</div>;
  }

  if (error) {
    return <p className="profile-modal__empty">{(error as Error).message}</p>;
  }

  if (data.items.length === 0) {
    return <p className="profile-modal__empty">No completed jobs yet.</p>;
  }

  return (
    <ul className="profile-modal__history">
      {data.items.map((job) => (
        <li key={job.id} className="profile-modal__history-item">
          <HistoryRow job={job} />
        </li>
      ))}
    </ul>
  );
}

function HistoryRow({ job }: { job: TesterRecentJob }) {
  const projectLabel = job.project
    ? `${job.project.organizationName} · ${job.project.name}`
    : 'Demo job';
  const timeLabel = formatTime(job.completedAt);
  const durationLabel = formatDuration(job.testDurationSeconds);
  return (
    <>
      <div className="profile-modal__history-top">
        <span className="profile-modal__history-project">{projectLabel}</span>
        <span className={`profile-modal__history-status profile-modal__history-status--${job.status}`}>
          {job.status}
        </span>
      </div>
      {(timeLabel || durationLabel) && (
        <div className="profile-modal__history-meta">
          {timeLabel && <span>{timeLabel}</span>}
          {timeLabel && durationLabel && <span className="profile-modal__sep">·</span>}
          {durationLabel && <span>{durationLabel}</span>}
        </div>
      )}
      {job.description && <p className="profile-modal__history-desc">{job.description}</p>}
    </>
  );
}

function formatTime(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(seconds: number | null): string | null {
  if (seconds === null) return null;
  const minutes = Math.max(0, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
